/**
 * A specialist's evaluation in words (docs/evaluation.md). The shapes are `@aioe/shared` evaluation.ts;
 * nothing here shows an id, a code or a raw score.
 */

import type { SpecialistEvaluationOrder, SpecialistEvaluationRecord, SpecialistEvaluationSummary } from '@aioe/shared';

import { whenWords } from './skillEvidenceWords.ts';

/** "92%". */
export const percent = (rate: number): string => `${Math.round(rate * 100)}%`;

/** "1 adversarial case failed", "no adversarial case failed". */
export function adversarialWords(n: number): string {
  if (n === 0) return 'no adversarial case failed';
  return `${n} adversarial ${n === 1 ? 'case' : 'cases'} failed`;
}

/** "Evaluated 3 days ago: 92% pass, 1 adversarial case failed". */
export const evaluationLine = (e: Pick<SpecialistEvaluationSummary, 'at' | 'passRate' | 'adversarialFailed'>, now?: number): string =>
  `Evaluated ${whenWords(e.at, now)}: ${percent(e.passRate)} pass, ${adversarialWords(e.adversarialFailed)}`;

/** The one mark a specialist's result shows: stale first, else the verdict. */
export function evaluationMark(e: Pick<SpecialistEvaluationSummary, 'verdict' | 'stale'>): { state: 'connected' | 'error' | 'disabled'; label: string } {
  if (e.stale) return { state: 'disabled', label: 'Stale' };
  return e.verdict === 'pass' ? { state: 'connected', label: 'Passed' } : { state: 'error', label: 'Did not pass' };
}

/** Why a result is stale, in words. */
export function staleWords(changed: SpecialistEvaluationRecord['changed']): string {
  const s = changed?.includes('specialist');
  const d = changed?.includes('dataset');
  if (s && d) return 'The specialist and the dataset have both changed since.';
  if (s) return 'The specialist’s instructions, role, skills or tools have changed since.';
  if (d) return 'The dataset has changed, or was removed, since.';
  return 'The specialist or the dataset has changed since.';
}

/** "Against Refund asks on mju-gpu-1, judged by qwen3-32b." */
export const evaluationMeta = (e: Pick<SpecialistEvaluationRecord, 'datasetName' | 'deviceName' | 'result'>): string =>
  `Against ${e.datasetName} on ${e.deviceName} with ${e.result.worker}, judged by ${e.result.judge}.`;

/** An outstanding or failed order, in words; undefined when there is nothing to say. */
export function orderWords(o: SpecialistEvaluationOrder | undefined, hasResult: boolean, now?: number): string | undefined {
  if (!o) return undefined;
  if (o.state === 'pending') return `${hasResult ? 'A new evaluation was asked of' : 'Asked of'} ${o.deviceName} ${whenWords(o.issuedAt, now)} against ${o.datasetName}; it answers when it next reports in.`;
  if (o.state === 'failed' || o.state === 'refused' || o.state === 'expired') return `The last evaluation on ${o.deviceName} did not run${o.message ? `: ${o.message.replace(/\.$/, '')}` : ''}.`;
  return undefined;
}

/** A node has a project open when a workspace it reports belongs to a project, or a remote-controlled session runs in a repository (as SkillEvidence.tsx reads it). */
export function hasProjectOpen(n: { workspaces: { projectId?: string }[]; remoteSessions?: { rootId?: string }[] }): boolean {
  return n.workspaces.some((w) => !!w.projectId) || (n.remoteSessions ?? []).some((s) => !!s.rootId);
}
